// Discussion d'un retour (porté du CommentThread de Faast) : fil avec
// réponses, réactions, mentions, « en train d'écrire », modification et
// suppression selon can_edit / can_delete, temps réel, lien profond vers un
// commentaire. ⌘/Ctrl+Entrée envoie, Entrée va à la ligne.
import type { JSX } from 'preact'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks'
import type { Author, Comment } from '@feedbaacks/sdk'
import { useFeedbaacks } from '../context'
import { AtSign, Mic, Pencil, Reply, Send, Smile, Trash } from '../icons'
import { Avatar, Button, IconButton, Prose, Spinner, useDismiss } from '../primitives'
import { useSelector } from '../store'
import {
  QUICK_EMOJIS, TYPING_THROTTLE_MS, TYPING_TTL_MS, insertMention, keptMentions, mentionQuery, replyCountLabel, threadOf, typingLabel,
} from '../thread'
import { absoluteDate, cx, errorMessage, formatRelative } from '../util'

// Événements temps réel qui changent le fil d'un retour.
const THREAD_EVENTS = new Set(['feedback.comment_created', 'feedback.comment_updated', 'feedback.comment_deleted', 'feedback.reaction'])

export function Comments({ itemId, count, onCountChange }: { itemId: string; count: number; onCountChange: (n: number) => void }): JSX.Element {
  const { client, t, realtime, store } = useFeedbaacks()
  const highlight = useSelector(store, (s) => s.highlightCommentId)
  const [list, setList] = useState<Comment[] | null>(null)
  const scrollRef = useRef<HTMLUListElement>(null)
  const atBottom = useRef(true)

  const load = useCallback(() => {
    client.comments.list(itemId, { limit: 500 }).then((r) => setList(r.data), () => setList((l) => l ?? []))
  }, [client, itemId])
  useEffect(load, [load])
  // Une réponse, une retouche, une réaction d'un autre arrive en temps réel.
  useEffect(() => realtime.on((e) => {
    if (e.item_id === itemId && THREAD_EVENTS.has(e.type)) load()
  }), [realtime, itemId, load])

  // Défilement : on suit le bas tant que l'on y est ; le lien profond amène
  // le commentaire ciblé à l'écran et prime.
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el || !list) return
    const target = highlight ? el.querySelector<HTMLElement>(`[data-comment-id="${highlight}"]`) : null
    if (target) {
      atBottom.current = false
      target.scrollIntoView({ block: 'center' })
    } else if (atBottom.current) el.scrollTop = el.scrollHeight
  }, [list?.length, highlight])

  const replace = (c: Comment) => setList((l) => (l ?? []).map((x) => (x.id === c.id ? c : x)))
  const added = (c: Comment) => {
    setList((l) => [...(l ?? []), c])
    onCountChange(count + 1)
  }
  const removed = (c: Comment) => {
    setList((l) => (l ?? []).filter((x) => x.id !== c.id))
    onCountChange(Math.max(0, count - 1))
  }

  const { top, replies } = threadOf(list ?? [])
  return (
    <section class="fb-comments" data-testid="feedbaacks-comments">
      <h4 class="fb-section-title">
        {t('detail_discussion_label')}
        {count > 0 && ` · ${count}`}
      </h4>
      {list === null ? (
        <Spinner />
      ) : (
        top.length > 0 && (
          <ul class="fb-comment-list" ref={scrollRef}
            onScroll={(e) => {
              const el = e.currentTarget
              atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 50
            }}>
            {top.map((c) => (
              <li key={c.id} class="fb-comment-thread">
                <Bubble itemId={itemId} comment={c} highlighted={highlight === c.id} onChange={replace} onRemove={removed} />
                <Replies itemId={itemId} parent={c} replies={replies.get(c.id) ?? []} highlight={highlight}
                  onChange={replace} onRemove={removed} onAdded={added} />
              </li>
            ))}
          </ul>
        )
      )}
      <Typing itemId={itemId} />
      <CommentComposer itemId={itemId} first={!top.length} onSent={added} />
    </section>
  )
}

/** Réponses d'un commentaire : repliées derrière leur compteur, comme Faast. */
function Replies({ itemId, parent, replies, highlight, onChange, onRemove, onAdded }: {
  itemId: string
  parent: Comment
  replies: Comment[]
  highlight: string | null
  onChange: (c: Comment) => void
  onRemove: (c: Comment) => void
  onAdded: (c: Comment) => void
}): JSX.Element | null {
  const { t, store } = useFeedbaacks()
  const replyTo = useSelector(store, (s) => s.replyToId)
  const containsHighlight = !!highlight && replies.some((r) => r.id === highlight)
  const [open, setOpen] = useState(containsHighlight)
  useEffect(() => {
    if (containsHighlight || replyTo === parent.id) setOpen(true)
  }, [containsHighlight, replyTo, parent.id])
  if (!replies.length && replyTo !== parent.id) return null
  return (
    <div class="fb-replies">
      {replies.length > 0 && (
        <button type="button" class="fb-link fb-small" aria-expanded={open} data-testid="feedbaacks-reply-count" onClick={() => setOpen((v) => !v)}>
          {open ? t('comment_reply_hide') : replyCountLabel(replies.length, t)}
        </button>
      )}
      {open && replies.map((r) => (
        <Bubble key={r.id} itemId={itemId} comment={r} highlighted={highlight === r.id} onChange={onChange} onRemove={onRemove} reply />
      ))}
      {replyTo === parent.id && (
        <CommentComposer itemId={itemId} parentId={parent.id} autoFocus
          onSent={(c) => {
            onAdded(c)
            setOpen(true)
            store.set({ replyToId: null })
          }}
          onCancel={() => store.set({ replyToId: null })} />
      )}
    </div>
  )
}

/** Un commentaire : en-tête, texte, mentions, réactions, gestes. */
function Bubble({ itemId, comment: c, highlighted, onChange, onRemove, reply }: {
  itemId: string
  comment: Comment
  highlighted: boolean
  onChange: (c: Comment) => void
  onRemove: (c: Comment) => void
  reply?: boolean
}): JSX.Element {
  const { client, t, locale, notify, confirm, store, userId } = useFeedbaacks()
  const [editing, setEditing] = useState<string | null>(null)
  const [picker, setPicker] = useState(false)
  const pickerRef = useRef<HTMLSpanElement>(null)
  useDismiss(picker, () => setPicker(false), pickerRef)

  const saveEdit = async () => {
    if (editing === null || !editing.trim()) return
    try {
      onChange(await client.comments.update(itemId, c.id, editing))
      setEditing(null)
    } catch (err) {
      notify('error', errorMessage(err, t('comment_failed')))
    }
  }
  const remove = async () => {
    if (!(await confirm({ title: t('comment_delete_title'), message: t('comment_delete_message'), confirmLabel: t('delete'), destructive: true }))) return
    try {
      await client.comments.remove(itemId, c.id)
      onRemove(c)
    } catch (err) {
      notify('error', errorMessage(err, t('comment_failed')))
    }
  }
  const react = async (emoji: string) => {
    setPicker(false)
    try {
      onChange(await client.comments.react(itemId, c.id, emoji))
    } catch (err) {
      notify('error', errorMessage(err, t('action_failed')))
    }
  }
  const mentionedNames = c.mentions.map((m) => m.text.replace(/^@/, ''))

  return (
    <article class={cx('fb-comment', reply && 'is-reply', highlighted && 'is-highlighted')} data-comment-id={c.id} data-testid="feedbaacks-comment">
      <Avatar name={c.author.name} src={c.author.avatar_url} />
      <div class="fb-grow">
        <div class="fb-comment-head">
          <span class="fb-strong">{c.author.name || t('anonymous')}</span>
          {c.source === 'voice' && (
            <span class="fb-faint" title={t('comment_voice_tooltip')} aria-label={t('comment_voice_tooltip')}>
              <Mic size={11} />
            </span>
          )}
          <span class="fb-faint" title={absoluteDate(c.created_at, locale)}>{formatRelative(c.created_at, locale, t)}</span>
          {c.edit_history?.length ? <span class="fb-faint">{t('comment_edited')}</span> : null}
          <span class="fb-grow" />
          <span class="fb-comment-actions">
            <span class="fb-popover-anchor" ref={pickerRef}>
              <IconButton label={t('comment_reaction')} testId="feedbaacks-react" onClick={() => setPicker((v) => !v)}>
                <Smile size={12} />
              </IconButton>
              {picker && (
                <span class="fb-popover fb-emoji-picker" role="menu" aria-label={t('comment_reaction')}>
                  {QUICK_EMOJIS.map((e) => (
                    <button key={e} type="button" role="menuitem" class="fb-emoji" onClick={() => void react(e)}>{e}</button>
                  ))}
                </span>
              )}
            </span>
            {!reply && (
              <IconButton label={t('comment_reply')} testId="feedbaacks-reply" onClick={() => store.set({ replyToId: c.id })}>
                <Reply size={12} />
              </IconButton>
            )}
            {c.can_edit && editing === null && (
              <IconButton label={t('edit')} onClick={() => setEditing(c.body)}>
                <Pencil size={12} />
              </IconButton>
            )}
            {c.can_delete && (
              <IconButton label={t('delete')} danger onClick={() => void remove()}>
                <Trash size={12} />
              </IconButton>
            )}
          </span>
        </div>
        {editing !== null ? (
          <div class="fb-comment-edit">
            <textarea class="fb-textarea" value={editing} rows={3} aria-label={t('edit')}
              ref={(el) => el?.focus()}
              onInput={(e) => setEditing(e.currentTarget.value)}
              onKeyDown={(e) => {
                // Comme Faast : Entrée enregistre (Maj+Entrée va à la ligne), Échap annule.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  void saveEdit()
                } else if (e.key === 'Escape') {
                  e.stopPropagation()
                  setEditing(null)
                }
              }} />
            <div class="fb-row-actions">
              <Button variant="ghost" onClick={() => setEditing(null)}>{t('cancel')}</Button>
              <Button variant="primary" onClick={() => void saveEdit()}>{t('save')}</Button>
            </div>
          </div>
        ) : (
          <Prose text={c.body} mentions={c.mentions} />
        )}
        {mentionedNames.length > 0 && <p class="fb-faint fb-small">{t('comment_mentioned_label', { names: mentionedNames.join(', ') })}</p>}
        {c.reactions.length > 0 && (
          <div class="fb-reactions" data-testid="feedbaacks-reactions">
            {c.reactions.map((r) => (
              <button key={r.emoji} type="button" class={cx('fb-reaction', r.users.includes(userId) && 'is-mine')}
                aria-pressed={r.users.includes(userId)} onClick={() => void react(r.emoji)}>
                <span>{r.emoji}</span>
                <span class="fb-mono">{r.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </article>
  )
}

/** Écrire un commentaire ou une réponse : @ propose les personnes qui voient
 *  le retour, la saisie prévient les autres lecteurs. */
function CommentComposer({ itemId, parentId, first, autoFocus, onSent, onCancel }: {
  itemId: string
  parentId?: string
  first?: boolean
  autoFocus?: boolean
  onSent: (c: Comment) => void
  onCancel?: () => void
}): JSX.Element {
  const { client, t, notify } = useFeedbaacks()
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [query, setQuery] = useState<{ start: number; query: string } | null>(null)
  const [people, setPeople] = useState<Author[]>([])
  const [active, setActive] = useState(0)
  const picked = useRef<Array<{ user: Author; text: string }>>([])
  const lastTyping = useRef(0)
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (autoFocus) ref.current?.focus()
  }, [autoFocus])
  useEffect(() => {
    if (!query) return
    const timer = setTimeout(() => client.comments.mentionable(itemId, query.query).then((r) => {
      setPeople(r.data)
      setActive(0)
    }, () => setPeople([])), 150)
    return () => clearTimeout(timer)
  }, [query?.query, client, itemId])

  const onInput = (el: HTMLTextAreaElement) => {
    setText(el.value)
    setQuery(mentionQuery(el.value, el.selectionStart ?? el.value.length))
    const now = Date.now()
    if (el.value.trim() && now - lastTyping.current > TYPING_THROTTLE_MS) {
      lastTyping.current = now
      void client.comments.typing(itemId).catch(() => {})
    }
  }
  const choose = (person: Author) => {
    const el = ref.current
    if (!el || !query) return
    const next = insertMention(text, query.start, el.selectionStart ?? text.length, person.name)
    picked.current.push({ user: person, text: `@${person.name}` })
    setText(next.text)
    setQuery(null)
    setTimeout(() => {
      el.focus()
      el.setSelectionRange(next.caret, next.caret)
    }, 0)
  }
  const send = async () => {
    const body = text.trim()
    if (!body || sending) return
    setSending(true)
    try {
      const c = await client.comments.create(itemId, body, { parentId, mentions: keptMentions(body, picked.current) })
      picked.current = []
      setText('')
      onSent(c)
    } catch (err) {
      notify('error', errorMessage(err, t('comment_failed')))
    } finally {
      setSending(false)
    }
  }
  const placeholder = parentId ? t('comment_reply_placeholder') : first ? t('comment_placeholder_first') : t('comment_placeholder')
  return (
    <div class={cx('fb-comment-composer', parentId && 'is-reply')}>
      <div class="fb-relative fb-grow-x">
        <textarea ref={ref} class="fb-textarea" rows={2} value={text} placeholder={placeholder} aria-label={placeholder}
          data-testid={parentId ? 'feedbaacks-reply-input' : 'feedbaacks-comment-input'}
          onInput={(e) => onInput(e.currentTarget)}
          onKeyDown={(e) => {
            if (query && people.length) {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault()
                setActive((i) => (i + (e.key === 'ArrowDown' ? 1 : people.length - 1)) % people.length)
                return
              }
              if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault()
                choose(people[active])
                return
              }
            }
            if (e.key === 'Escape') {
              if (query) {
                e.stopPropagation()
                setQuery(null)
              } else if (onCancel) {
                e.stopPropagation()
                onCancel()
              }
            } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault()
              void send()
            }
          }} />
        {query && (
          <ul class="fb-popover fb-mention-menu" role="listbox" aria-label={t('comment_mention_title')} data-testid="feedbaacks-mention-menu">
            {people.length === 0 ? (
              <li class="fb-faint fb-small">{t('comment_mention_none')}</li>
            ) : (
              people.map((p, i) => (
                <li key={p.id} role="option" aria-selected={i === active} class={cx('fb-mention-option', i === active && 'is-active')}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    choose(p)
                  }}>
                  <Avatar name={p.name} src={p.avatar_url} />
                  <span>{p.name}</span>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
      <div class="fb-row-actions">
        <IconButton label={t('comment_mention_title')} onClick={() => {
          const el = ref.current
          if (!el) return
          const caret = el.selectionStart ?? text.length
          const prefix = caret > 0 && !/\s/.test(text[caret - 1]) ? ' @' : '@'
          const next = text.slice(0, caret) + prefix + text.slice(caret)
          setText(next)
          setQuery({ start: caret + prefix.length - 1, query: '' })
          setTimeout(() => {
            el.focus()
            el.setSelectionRange(caret + prefix.length, caret + prefix.length)
          }, 0)
        }}>
          <AtSign size={14} />
        </IconButton>
        {onCancel && <Button variant="ghost" onClick={onCancel}>{t('cancel')}</Button>}
        <Button variant="primary" onClick={() => void send()} disabled={!text.trim() || sending}
          testId={parentId ? 'feedbaacks-reply-send' : 'feedbaacks-comment-send'}>
          {sending ? <Spinner size={14} /> : <Send size={14} />}
          {parentId ? t('comment_reply') : t('comment_send')}
        </Button>
      </div>
    </div>
  )
}

/** « Sam écrit… » : signaux des autres lecteurs, oubliés après quelques secondes. */
function Typing({ itemId }: { itemId: string }): JSX.Element | null {
  const { realtime, t, userId } = useFeedbaacks()
  const [typing, setTyping] = useState<Record<string, { name: string; at: number }>>({})
  useEffect(() => realtime.on((e) => {
    if (e.item_id !== itemId) return
    const who = String(e.data?.user_id ?? '')
    if (e.type === 'feedback.typing' && who && who !== userId) {
      setTyping((s) => ({ ...s, [who]: { name: String(e.data?.name || t('anonymous')), at: Date.now() } }))
    } else if (e.type === 'feedback.comment_created') {
      setTyping({}) // le message est arrivé
    }
  }), [realtime, itemId, userId, t])
  useEffect(() => {
    if (!Object.keys(typing).length) return
    const timer = setInterval(() => setTyping((s) => {
      const fresh = Object.fromEntries(Object.entries(s).filter(([, v]) => Date.now() - v.at < TYPING_TTL_MS))
      return Object.keys(fresh).length === Object.keys(s).length ? s : fresh
    }), 1000)
    return () => clearInterval(timer)
  }, [typing])
  const label = typingLabel(Object.values(typing).map((v) => v.name), t)
  if (!label) return null
  return <p class="fb-typing fb-faint fb-small" role="status" data-testid="feedbaacks-typing">{label}</p>
}
